import { useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import {
  confirmMfaEnrollment,
  disableMfa,
  fetchMfaStatus,
  setupMfaEnrollment,
} from '../features/iso27001/security/securityApi';
import type { MfaSetupResponse, MfaStatus } from '../features/iso27001/security/types';
import { showToast } from './ui/toast.utils';
import { Button } from './ui/button';
import { Input } from './ui/input';

export function MfaEnrollmentForm() {
  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [setup, setSetup] = useState<MfaSetupResponse | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMfaStatus();
      setStatus(data);
    } catch {
      showToast('No se pudo cargar el estado MFA', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (!setup?.otpauthUri) {
      setQrDataUrl(null);
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(setup.otpauthUri, { width: 200, margin: 1 })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [setup?.otpauthUri]);

  const handleStartSetup = async () => {
    setBusy(true);
    try {
      const data = await setupMfaEnrollment();
      setSetup(data);
      setCode('');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      showToast(axiosErr.response?.data?.error || 'No se pudo iniciar el enrolamiento', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      await confirmMfaEnrollment(code.trim());
      setSetup(null);
      setCode('');
      showToast('MFA activado correctamente', 'success');
      await loadStatus();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      showToast(axiosErr.response?.data?.error || 'Código incorrecto', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      await disableMfa();
      setSetup(null);
      showToast('MFA desactivado', 'success');
      await loadStatus();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      showToast(axiosErr.response?.data?.error || 'No se pudo desactivar MFA', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading || !status) {
    return <p className='text-sm text-muted-foreground'>Cargando autenticación de dos factores...</p>;
  }

  return (
    <div className='space-y-4'>
      <p className='text-sm text-muted-foreground'>
        {status.enabled
          ? 'Tu cuenta tiene MFA TOTP activo.'
          : status.requiredByPolicy
            ? 'Tu rol requiere MFA. Activá la autenticación de dos factores para poder ingresar.'
            : 'Activá MFA opcional con una app autenticadora (Google Authenticator, Authy, etc.).'}
      </p>

      {status.enabled ? (
        <div className='space-y-2'>
          <p className='text-sm text-green-600 dark:text-green-400'>Estado: activo</p>
          {status.canDisable && (
            <Button type='button' variant='outline' disabled={busy} onClick={() => void handleDisable()}>
              Desactivar MFA
            </Button>
          )}
          {!status.canDisable && (
            <p className='text-xs text-muted-foreground'>
              MFA es obligatorio para tu rol y no puede desactivarse.
            </p>
          )}
        </div>
      ) : (
        <div className='space-y-4'>
          {!setup && (
            <Button type='button' disabled={busy} onClick={() => void handleStartSetup()}>
              Configurar MFA
            </Button>
          )}

          {setup && (
            <div className='space-y-3 border border-border rounded-md p-4'>
              {qrDataUrl && (
                <img src={qrDataUrl} alt='Código QR MFA' className='mx-auto rounded border border-border' />
              )}
              <div>
                <p className='text-xs text-muted-foreground mb-1'>Clave manual (si no podés escanear el QR):</p>
                <code className='text-xs break-all bg-muted px-2 py-1 rounded'>{setup.secret}</code>
              </div>
              <div className='space-y-2'>
                <Input
                  type='text'
                  inputMode='numeric'
                  autoComplete='one-time-code'
                  placeholder='Código de 6 dígitos'
                  value={code}
                  maxLength={8}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />
                <div className='flex gap-2'>
                  <Button type='button' disabled={busy || code.length < 6} onClick={() => void handleConfirm()}>
                    Confirmar MFA
                  </Button>
                  <Button
                    type='button'
                    variant='outline'
                    disabled={busy}
                    onClick={() => {
                      setSetup(null);
                      setCode('');
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
